import { Grid } from '@material-ui/core';
import React from 'react';

/**
 * This component is used to create constantly sized grid elements when it does not have children.
 * If used within a 'column' grid container, the size will be height.
 * If used with a 'row' grid container, the size will be the width.
 * If children are provided to the component and their dimensions are larger than the size,
 * this component will grow in size.
 *
 * Usage: `<SizedGridItem size='10px' />
 *
 * @param props size - should be a valid dimension for flex-basis - https://developer.mozilla.org/en-US/docs/Web/CSS/flex-basis#Syntax
 */
const SizedGridItem: React.FC<ISizedGridItemProps> = (
  props: React.PropsWithChildren<ISizedGridItemProps>,
): JSX.Element => {
  const flexStyle: React.CSSProperties = {
    flex: `0 0 ${props.size}`,
  };

  const className = props.className !== undefined ? props.className : '';
  const style = { ...props.style, ...flexStyle };

  // BM - Ideally we don't use inline styles but using makeStyles here, the class name will regenerate
  // potentially many times on a state update. This lets us avoid those additional compute cycles if the browser
  // has the re-evaluate the styling.
  return (
    <Grid item className={className} style={style} id={props.id} data-testid={props.dataTestId}>
      {props.children}
    </Grid>
  );
};

interface ISizedGridItemProps {
  size: string;
  style?: React.CSSProperties | undefined;
  className?: string;
  id?: string;
  dataTestId?: string;
}

export default SizedGridItem;
